import type { WorkerBatchCommand, WorkerBatchResult } from './workerBatchContract'

export type { WorkerBatchCommand, WorkerBatchResult } from './workerBatchContract'

const configuredApiBase = import.meta.env.VITE_TIMBUS_API_URL?.trim()
const apiBase = configuredApiBase || (import.meta.env.DEV ? 'http://127.0.0.1:8787' : '')

export class WorkerUnauthorizedError extends Error {
  constructor() {
    super('Worker session is invalid')
    this.name = 'WorkerUnauthorizedError'
  }
}

export class WorkerLoginError extends Error {
  readonly kind: 'credentials' | 'network'

  constructor(kind: 'credentials' | 'network', message: string) {
    super(message)
    this.name = 'WorkerLoginError'
    this.kind = kind
  }
}

export interface WorkerLoginResponse {
  token: string
}

const TOKEN_PATTERN = /^v1\.[A-Za-z0-9_-]{43}$/

export function isWorkerSessionToken(value: unknown): value is string {
  return typeof value === 'string' && TOKEN_PATTERN.test(value)
}

function requireApiBase() {
  if (!apiBase) throw new Error('VITE_TIMBUS_API_URL is not configured')
  return apiBase.replace(/\/+$/, '')
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown
  } catch {
    return undefined
  }
}

function isLoginPayload(value: unknown): value is WorkerLoginResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'token' in value &&
    isWorkerSessionToken(value.token)
  )
}

export async function loginToWorker(
  username: string,
  password: string,
): Promise<WorkerLoginResponse> {
  let response: Response
  try {
    response = await fetch(new URL('auth/login', `${requireApiBase()}/`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    })
  } catch {
    throw new WorkerLoginError('network', 'Worker is unavailable')
  }

  const body = await readJson(response)
  if (response.status === 401) throw new WorkerLoginError('credentials', 'Invalid credentials')
  if (!response.ok) throw new WorkerLoginError('network', 'Worker is unavailable')
  if (!isLoginPayload(body))
    throw new WorkerLoginError('network', 'Worker returned an invalid session')
  return body
}

export interface WorkerRequestOptions {
  session?: string
  signal?: AbortSignal
}

export const WORKER_BATCH_LIMIT = 50

interface WorkerBatchResponse {
  results: WorkerBatchResult[]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isBatchResult(value: unknown): value is WorkerBatchResult {
  if (!isRecord(value)) return false
  return (
    typeof value.id === 'string' &&
    typeof value.status === 'number' &&
    Number.isInteger(value.status) &&
    value.status >= 100 &&
    value.status <= 599 &&
    (value.error === undefined || value.error === 'upstream-unavailable')
  )
}

function decodeBatchResponse(value: unknown, expectedIds: Set<string>): WorkerBatchResponse {
  if (!isRecord(value) || !Array.isArray(value.results)) {
    throw new Error('Worker returned an invalid batch response')
  }

  const results: WorkerBatchResult[] = []
  const seen = new Set<string>()
  for (const result of value.results) {
    if (!isBatchResult(result) || !expectedIds.has(result.id) || seen.has(result.id)) {
      throw new Error('Worker returned an invalid batch response')
    }
    seen.add(result.id)
    results.push(result)
  }
  if (seen.size !== expectedIds.size)
    throw new Error('Worker returned an incomplete batch response')
  return { results }
}

/** Fetch an allowlisted Worker relay command with the current bearer session. */
export async function fetchWorkerCommand(
  command: string,
  { session, signal }: WorkerRequestOptions = {},
) {
  let response: Response
  const headers = new Headers({ Accept: 'application/json' })
  if (session) headers.set('Authorization', `Bearer ${session}`)
  response = await fetch(new URL(command.replace(/^\/+/, ''), `${requireApiBase()}/`), {
    method: 'GET',
    headers,
    signal,
  })
  if (response.status === 401) throw new WorkerUnauthorizedError()
  return response
}

/**
 * Execute logical VehicleState requests through the authenticated Worker batch.
 *
 * Chunks are sequential so a future dataset above the Worker limit cannot turn
 * one browser refresh into multiple simultaneous six-connection pools.
 */
export async function fetchWorkerBatch(
  commands: WorkerBatchCommand[],
  { session, signal }: WorkerRequestOptions = {},
): Promise<WorkerBatchResult[]> {
  const commandIds = new Set(commands.map((command) => command.id))
  if (commandIds.size !== commands.length)
    throw new Error('Worker batch command IDs must be unique')

  const resultsById = new Map<string, WorkerBatchResult>()
  for (let index = 0; index < commands.length; index += WORKER_BATCH_LIMIT) {
    signal?.throwIfAborted()
    const chunk = commands.slice(index, index + WORKER_BATCH_LIMIT)
    const expectedIds = new Set(chunk.map((command) => command.id))
    const headers = new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
    })
    if (session) headers.set('Authorization', `Bearer ${session}`)

    const response = await fetch(new URL('batch', `${requireApiBase()}/`), {
      method: 'POST',
      headers,
      body: JSON.stringify({ requests: chunk }),
      signal,
    })
    if (response.status === 401) throw new WorkerUnauthorizedError()
    if (!response.ok) throw new Error(`Worker batch returned HTTP ${response.status}`)

    const decoded = decodeBatchResponse(await readJson(response), expectedIds)
    for (const result of decoded.results) resultsById.set(result.id, result)
  }

  // Return caller order rather than trusting response order. The metadata in
  // timbusApi is positional, so this correlation is a correctness boundary.
  return commands.map((command) => resultsById.get(command.id)!)
}
